import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import {
  Icon,
  PageShell,
  Pagination,
  SkeletonBar,
  inkA,
  tokens,
  useIsMobile,
} from 'components/v3';
import useAdminRouteContext from 'hooks/useAdminRouteContext';
import { fieldErrorsOf } from 'shared/apolloFieldErrors';
import storage from 'shared/storage';
import { w9DashboardPath } from '../../W9/shared/routes';
import FormShellHeader from '../components/FormShellHeader';
import {
  useFormSubmissionsExport,
  useGetFormAdmin,
  useGetFormSubmissions,
  useGetFormSubmissionStats,
  useSetFormStatus,
} from '../gql';
import { FormsHelp } from '../help';
import { brand } from '../shared/brand';
import { downloadUrl } from '../shared/exporters/browser';
import {
  formBuilderPath,
  formsListPath,
  formSubmissionsPath,
} from '../shared/routes';
import { submissionsTourNavigate } from '../shared/tourNavigation';
import useFormsToaster from '../shared/useFormsToaster';
import EntriesChart from './EntriesChart';
import EntryModal from './entry/EntryModal';
import { columnsById } from './exportMatrix';
import { exportedToast, exportRunInput } from './exportRun';
import PublicLinkBar from './PublicLinkBar';
import StatsRow from './StatsRow';
import SubmissionsTable, { DEFAULT_SORT, nextSort } from './SubmissionsTable';
import SubmissionsToolbar from './SubmissionsToolbar';
import { indexVersionFields } from './versionFields';
import ColumnsModal from './views/ColumnsModal';
import ExportModal from './views/ExportModal';
import {
  DEFAULT_VIEW_ID,
  catalogKeys,
  catalogOf,
  colDef,
  defaultCols,
  loadExportChoice,
  loadViews,
  newViewId,
  resolveViews,
  saveExportChoice,
  saveViews,
} from './views/viewModel';
import ViewTabs from './views/ViewTabs';

// A form's Submissions tab (build plan §5.7, T27–T30; Oct 7 design): the
// shared form header, the public link bar (public-form plan §4), three stat
// tiles, the 14-week chart, search + Export, and the entries table under
// its saved-view tabs. A row opens the entry card in a modal. Search, sort
// and paging go to the server and live in component state; the URL keeps
// only ?entry=<id>, so an entry link opens straight into its card. Saved
// views and the last export choice are kept per form and per user in this
// browser (views/viewModel). A `w9` form's entries live in the W-9 section
// (w9-plan §6.1): its id here redirects there, so an old email link lands.

export const PAGE_SIZE = 25;

const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const readEntryParam = (search) => {
  const n = parseInt(new URLSearchParams(search || '').get('entry'), 10);
  return n > 0 ? n : null;
};

const listInput = (formId, { search, sort }) => ({
  form_id: formId,
  ...(search ? { search } : {}),
  sort: sort.key,
  direction: sort.direction,
});

const emptyBodyFor = (form) =>
  form && form.status !== 'live'
    ? `This form is a Draft. Go Live so people can find it in the ${
        brand().name
      } app and at its web link.`
    : `Entries show up here as soon as people submit this form in the ${
        brand().name
      } app or on the web.`;

const signedInUserId = () => {
  const user = storage.get('user', {});
  return (user && user.id) || null;
};

// Definitions newest first: the builder's working copy, then every
// version the list returned. Used to group fields by section.
const definitionsOf = (form, versions) => [
  ...(form && form.working_version && form.working_version.definition
    ? [form.working_version.definition]
    : []),
  ...[...(versions || [])]
    .sort((a, b) => (b.version_number || 0) - (a.version_number || 0))
    .map((v) => v.definition),
];

// Last settled list, so a new search or page keeps showing the previous
// rows (instead of a skeleton flash) until the server answers. A failed
// load forgets them: stale rows must not pass for the current results.
const useSettledList = (list) => {
  const last = useRef(null);
  const payload =
    !list.error && list.data ? list.data.getFormSubmissions : null;
  const current = useMemo(
    () =>
      payload
        ? {
            rows: payload.results || [],
            count: payload.count || 0,
            columns: payload.columns || [],
            versions: payload.versions || [],
          }
        : null,
    [payload]
  );
  if (list.error) last.current = null;
  if (current) last.current = current;
  return current || last.current;
};

// The list query's message once it has failed (not while a retry runs).
export const listErrorOf = (list) =>
  list.error && !list.loading ? fieldErrorsOf(list.error).global : null;

// refetch() rejects when the retry fails too; the hook's error shows that.
const retry = (list) => () => {
  if (typeof list.refetch !== 'function') return;
  Promise.resolve(list.refetch()).catch(() => undefined);
};

const NotFound = ({ message, prefix }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: '56px 20px',
        textAlign: 'center',
        borderRadius: 16,
        background: t.card,
        border: '1px solid ' + t.border,
        color: t.sub,
      }}
    >
      <Icon name="file-text" size={30} color={inkA(theme, 0.3)} />
      <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: t.text }}>
        {message}
      </h1>
      <Link
        to={formsListPath(prefix)}
        style={{ color: t.primary, fontWeight: 700, textDecoration: 'none' }}
      >
        Back to Forms
      </Link>
    </div>
  );
};

NotFound.propTypes = {
  message: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
};

const HeaderSkeleton = ({ isMobile }) => {
  const t = tokens(useTheme());
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        minHeight: isMobile ? 100 : 60,
        padding: '12px 20px',
        margin: isMobile ? '-16px -12px 12px' : '0 0 16px',
        boxSizing: 'border-box',
        background: t.card,
        border: isMobile ? 'none' : '1px solid ' + t.border,
        borderBottom: '1px solid ' + t.border,
        borderRadius: isMobile ? 0 : 16,
      }}
    >
      <SkeletonBar $w="220px" $h="16px" />
    </div>
  );
};

HeaderSkeleton.propTypes = { isMobile: PropTypes.bool.isRequired };

const statusToast = (status) =>
  status === 'live'
    ? `Form is now Live in the ${brand().name} app`
    : 'Form switched to Draft — hidden in the app';

const SubmissionsDashboard = () => {
  const { id } = useParams();
  const formId = parseInt(id, 10) || null;
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { routePrefix } = useAdminRouteContext();
  const { showToast, toasterNode } = useFormsToaster();
  const entryId = readEntryParam(location.search);
  const userId = useMemo(signedInUserId, []);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [statusBusy, setStatusBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // 'cols' | 'new' | 'export'
  const [storedViews, setStoredViews] = useState(() =>
    loadViews(formId, userId)
  );
  const [lastExport, setLastExport] = useState(() =>
    loadExportChoice(formId, userId)
  );
  const pendingNav = useRef(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const { form, error: formError } = useGetFormAdmin(formId);
  const w9 = !!form && form.kind === 'w9';
  useEffect(() => {
    if (!w9) return;
    history.replace(
      w9DashboardPath(routePrefix, {
        account: form.account ? form.account.id : null,
        entry: entryId,
      })
    );
  }, [w9, form, history, routePrefix, entryId]);
  const statsQuery = useGetFormSubmissionStats(formId, {
    fetchPolicy: 'cache-and-network',
    skip: w9,
  });
  const filters = listInput(formId, { search, sort });
  const list = useGetFormSubmissions(
    { ...filters, page, limit: PAGE_SIZE },
    { fetchPolicy: 'cache-and-network', skip: w9 }
  );
  const settled = useSettledList(list);
  const setFormStatus = useSetFormStatus();
  const exportSubmissions = useFormSubmissionsExport();

  const rows = settled ? settled.rows : [];
  const count = settled ? settled.count : 0;
  const serverColumns = settled ? settled.columns : null;
  const versionIndex = useMemo(
    () => indexVersionFields(settled && settled.versions),
    [settled]
  );
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const stats = statsQuery.stats;
  const statsLoading = !stats && !!statsQuery.loading;

  // ---- saved views ----
  const catalog = useMemo(
    () =>
      catalogOf(
        serverColumns,
        definitionsOf(form, settled && settled.versions)
      ),
    [serverColumns, form, settled]
  );
  const keys = useMemo(
    () => (serverColumns ? catalogKeys(catalog) : []),
    [catalog, serverColumns]
  );
  const defaults = useMemo(() => defaultCols(serverColumns), [serverColumns]);
  const vs = useMemo(
    () => resolveViews(storedViews, keys, defaults),
    [storedViews, keys, defaults]
  );
  const activeView = vs.views.find((v) => v.id === vs.active) || vs.views[0];
  const tableColumns = useMemo(() => {
    const byId = columnsById(serverColumns);
    const cols = activeView.cols.map((k) => colDef(k, byId)).filter(Boolean);
    return cols.length
      ? cols
      : defaults.map((k) => colDef(k, byId)).filter(Boolean);
  }, [activeView, serverColumns, defaults]);

  const updateViews = (change) => {
    const next = change(vs);
    setStoredViews(next);
    saveViews(formId, userId, next);
  };

  const onSaveColumns = ({ name, cols }) => {
    if (dialog === 'new') {
      const idForView = newViewId();
      updateViews((cur) => ({
        active: idForView,
        views: cur.views.concat({ id: idForView, name, cols }),
      }));
      showToast(`View “${name}” created`);
    } else {
      updateViews((cur) => ({
        ...cur,
        views: cur.views.map((v) =>
          v.id === activeView.id ? { ...v, name, cols } : v
        ),
      }));
      showToast(`View “${name}” saved`);
    }
    setDialog(null);
  };

  const onDeleteView = () => {
    updateViews((cur) => ({
      active: DEFAULT_VIEW_ID,
      views: cur.views.filter((v) => v.id !== activeView.id),
    }));
    showToast(`View “${activeView.name}” deleted`);
    setDialog(null);
  };

  const onSaveViewFromExport = (name, cols) => {
    const idForView = newViewId();
    updateViews((cur) => ({
      ...cur,
      views: cur.views.concat({ id: idForView, name, cols }),
    }));
    showToast(`View “${name}” saved · find it above the table`);
  };

  // ---- export ----
  // The worker builds the file; the dialog shows "Preparing…" until it is
  // ready, then the download starts and the dialog closes. A failure keeps
  // the dialog open (false) so the person can try again.
  const onRunExport = async ({ fmt, cols, scope }) => {
    const choice = { fmt, cols };
    saveExportChoice(formId, userId, choice);
    setLastExport(choice);
    const { data, error } = await exportSubmissions(
      exportRunInput({ formId, fmt, cols, scope, search, sort })
    );
    if (!mounted.current) return false;
    if (error) {
      showToast({ type: 'error', title: error.global });
      return false;
    }
    downloadUrl(data.url, data.run.filename);
    showToast(exportedToast(fmt, data.run.rows));
    return true;
  };

  // ---- entries ----
  const entryPath = useCallback(
    (entry) => formSubmissionsPath(routePrefix, formId, entry ? { entry } : {}),
    [routePrefix, formId]
  );
  const openEntry = (row) => history.replace(entryPath(row.id));
  const closeEntry = () => history.replace(entryPath(null));

  // Prev / Next run across pages: past the end of this page, load the next
  // one and open its first (or last) entry once it arrives.
  useEffect(() => {
    if (!pendingNav.current || list.loading || !settled) return;
    const target =
      pendingNav.current === 'first'
        ? settled.rows[0]
        : settled.rows[settled.rows.length - 1];
    pendingNav.current = null;
    if (target) history.replace(entryPath(target.id));
  }, [settled, list.loading, history, entryPath]);

  const at = rows.findIndex((r) => r.id === entryId);
  const nav =
    at >= 0
      ? {
          index: (page - 1) * PAGE_SIZE + at,
          total: count,
          onPrev: () => {
            if (at > 0) openEntry(rows[at - 1]);
            else if (page > 1) {
              pendingNav.current = 'last';
              setPage(page - 1);
            }
          },
          onNext: () => {
            if (at < rows.length - 1) openEntry(rows[at + 1]);
            else if (page < pageCount) {
              pendingNav.current = 'first';
              setPage(page + 1);
            }
          },
        }
      : null;

  // ---- product tour (help/FormsHelp) ----
  // 'entry' and 'export' that arrive before the first rows load wait here.
  const tourLater = useRef(null);
  const tourReady = !!settled && !!form;
  const tourNavigate = submissionsTourNavigate({
    formId,
    rows,
    ready: tourReady,
    canExport: tourReady && count > 0,
    routePrefix,
    push: (path) => history.push(path),
    openEntry,
    openExport: () => setDialog('export'),
    closeOverlays: () => {
      setDialog(null);
      if (entryId) closeEntry();
    },
    later: (what) => {
      tourLater.current = what;
    },
  });
  useEffect(() => {
    if (!tourLater.current || !settled || !form) return;
    const want = tourLater.current;
    tourLater.current = null;
    if (want === 'entry' && settled.rows[0]) {
      history.replace(entryPath(settled.rows[0].id));
    } else if (want === 'export' && settled.count > 0) {
      setDialog('export');
    }
  }, [settled, form, history, entryPath]);

  const onSearch = (text) => {
    setSearch(text.trim());
    setPage(1);
  };
  const onSort = (key) => {
    setSort((cur) => nextSort(cur, key));
    setPage(1);
  };

  const onSetStatus = async (next) => {
    setStatusBusy(true);
    const { error } = await setFormStatus({ id: formId, status: next });
    if (!mounted.current) return;
    setStatusBusy(false);
    if (!error) {
      showToast(statusToast(next));
      return;
    }
    const fixable = Object.keys(error.fields || {}).length > 0;
    showToast({
      type: 'error',
      title: error.global,
      action: fixable
        ? {
            text: 'Open builder',
            onClick: () => history.push(formBuilderPath(routePrefix, formId)),
          }
        : undefined,
    });
  };

  if (formError && !form) {
    return (
      <>
        <NotFound
          message={fieldErrorsOf(formError).global}
          prefix={routePrefix}
        />
        {toasterNode}
      </>
    );
  }
  if (w9) return <HeaderSkeleton isMobile={isMobile} />;

  return (
    <>
      {form ? (
        <>
          <h1 style={VISUALLY_HIDDEN}>{`${form.name} submissions`}</h1>
          <FormShellHeader
            form={form}
            activeTab="submissions"
            statusBusy={statusBusy}
            onSetStatus={onSetStatus}
          />
        </>
      ) : (
        <HeaderSkeleton isMobile={isMobile} />
      )}

      <div style={{ display: 'grid', gap: isMobile ? 14 : 16 }}>
        {form ? <PublicLinkBar form={form} onToast={showToast} /> : null}
        <StatsRow stats={stats} loading={statsLoading} isMobile={isMobile} />
        <EntriesChart
          weekly={(stats && stats.weekly) || []}
          total={stats ? stats.weekly_total : 0}
          average={stats ? stats.weekly_average : 0}
          loading={statsLoading}
          isMobile={isMobile}
        />
        <SubmissionsToolbar
          count={count}
          counting={!settled}
          onSearch={onSearch}
          onExport={() => setDialog('export')}
          canExport={!!form && !!settled && count > 0}
          isMobile={isMobile}
        />
        <SubmissionsTable
          columns={tableColumns}
          versionIndex={versionIndex}
          rows={rows}
          sort={sort}
          onSort={onSort}
          onOpen={openEntry}
          activeId={entryId}
          loading={!!list.loading}
          searching={!!search}
          emptyBody={emptyBodyFor(form)}
          error={listErrorOf(list)}
          onRetry={retry(list)}
          header={
            <ViewTabs
              views={vs.views}
              activeId={vs.active}
              columnCount={tableColumns.length}
              onPick={(viewId) =>
                updateViews((cur) => ({ ...cur, active: viewId }))
              }
              onNew={() => setDialog('new')}
              onColumns={() => setDialog('cols')}
            />
          }
          footer={
            pageCount > 1 ? (
              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={setPage}
              />
            ) : null
          }
        />
      </div>

      <EntryModal
        entryId={entryId}
        preview={rows.find((r) => r.id === entryId) || null}
        form={form}
        nav={nav}
        isMobile={isMobile}
        onClose={closeEntry}
        showToast={showToast}
      />
      {dialog === 'cols' || dialog === 'new' ? (
        <ColumnsModal
          catalog={catalog}
          isNew={dialog === 'new'}
          view={
            dialog === 'new'
              ? { id: null, name: '', cols: activeView.cols }
              : activeView
          }
          defaults={defaults}
          onSave={onSaveColumns}
          onDelete={onDeleteView}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'export' && form ? (
        <ExportModal
          catalog={catalog}
          keys={keys}
          views={vs.views}
          activeId={vs.active}
          formName={form.name}
          lastChoice={lastExport}
          searching={!!search}
          matchingCount={count}
          allCount={stats ? stats.total : count}
          onRun={onRunExport}
          onSaveView={onSaveViewFromExport}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {toasterNode}
      <FormsHelp page="submissions" navigate={tourNavigate} />
    </>
  );
};

// Keyed by form id: moving to another form's submissions starts fresh
// (search, sort, page and the last settled rows all reset).
const FormSubmissions = () => {
  const { id } = useParams();
  return (
    <PageShell>
      <SubmissionsDashboard key={id} />
    </PageShell>
  );
};

export default FormSubmissions;
